import { action, computed, makeObservable, observable } from "mobx"

import type { IManualClPositionRepository } from "../interfaces"
import { ManualClPositionStore } from "./manual-cl-position-store"

/**
 * Observable repository of `ManualClPositionStore` instances.
 *
 * Keyed by the composite GraphQL `positionId` (`<owner>-<pool>-<tickLower>-<tickUpper>-<chainId>`)
 * which is the only id guaranteed stable across mint / burn / collect events
 * before indexer Proposal 4 ships. A secondary index off the synthetic /
 * real `tokenId.toString()` keeps `find(tokenId)` look-ups O(1) for the
 * manage-position flow, existing routes and unit tests (which pass a bigint
 * tokenId as the lookup key).
 */
/**
 * Telemetry describing the last NFPM discovery pass against the connected
 * wallet. Surfaced to the UI so a user with more NFPM NFTs than the
 * discovery cap sees an explicit "some positions not shown" banner rather
 * than a silent truncation tucked away in `console.warn`.
 */
export type NfpmDiscoverySnapshot = {
  totalOwned: number
  scanned: number
  truncated: boolean
}

export class ManualClPositionRepository implements IManualClPositionRepository {
  private static readonly POST_CREATE_PENDING_MS = 30_000

  positions = new Map<string, ManualClPositionStore>()
  /**
   * `tokenId.toString()` -> `positionId` secondary index. Kept in sync with
   * `positions` inside `upsert` / `remove` so lookups don't have to iterate.
   */
  private tokenIdIndex = new Map<string, string>()
  private postCreatePendingUntilByTokenId = new Map<string, number>()
  isHydrated: boolean = false
  /**
   * Last known state of the NFPM discovery scan (`balanceOf` + N
   * `tokenOfOwnerByIndex`). Undefined before the first scan finishes.
   */
  nfpmDiscovery: NfpmDiscoverySnapshot | undefined = undefined

  constructor() {
    makeObservable(this, {
      positions: observable,
      postCreatePendingUntilByTokenId: observable,
      isHydrated: observable,
      nfpmDiscovery: observable,
      markHydrated: action,
      reset: action,
      upsert: action,
      remove: action,
      markPostCreatePending: action,
      clearPostCreatePending: action,
      setNfpmDiscovery: action,
      activePositions: computed,
      totalPositions: computed,
    })
  }

  setNfpmDiscovery(snapshot: NfpmDiscoverySnapshot | undefined): void {
    this.nfpmDiscovery = snapshot
  }

  /**
   * Idempotent insert/update. Preserves store identity so observers that
   * already hold a reference continue to react through repeated refreshes.
   */
  upsert(position: ManualClPositionStore): ManualClPositionStore {
    const key = position.positionId
    const existing = this.positions.get(key)
    if (existing) {
      this.tokenIdIndex.set(existing.tokenId.toString(), key)
      this.clearPostCreatePending(existing.tokenId)
      if (existing.nfpmTokenId !== undefined) {
        this.clearPostCreatePending(existing.nfpmTokenId)
      }
      return existing
    }
    this.positions.set(key, position)
    this.tokenIdIndex.set(position.tokenId.toString(), key)
    this.clearPostCreatePending(position.tokenId)
    if (position.nfpmTokenId !== undefined) {
      this.clearPostCreatePending(position.nfpmTokenId)
    }
    return position
  }

  markPostCreatePending(tokenId: bigint | string): void {
    this.postCreatePendingUntilByTokenId.set(
      tokenId.toString(),
      Date.now() + ManualClPositionRepository.POST_CREATE_PENDING_MS,
    )
  }

  clearPostCreatePending(tokenId: bigint | string): void {
    this.postCreatePendingUntilByTokenId.delete(tokenId.toString())
  }

  isPostCreatePending(tokenId: bigint | string): boolean {
    const pendingUntil = this.postCreatePendingUntilByTokenId.get(tokenId.toString())
    return pendingUntil !== undefined && pendingUntil > Date.now()
  }

  /**
   * Find a position by either its GraphQL `positionId` or its synthetic /
   * real `tokenId` (passed as bigint or string). The secondary token-id
   * index means existing call sites that know only the tokenId keep
   * working.
   */
  find(lookup: bigint | string): ManualClPositionStore | undefined {
    const needle = typeof lookup === "bigint" ? lookup.toString() : lookup
    // Composite positionId keys never contain a bigint, so only string
    // lookups can hit the primary map directly.
    if (typeof lookup === "string") {
      const direct = this.positions.get(lookup)
      if (direct) return direct
    }
    const pid = this.tokenIdIndex.get(needle)
    if (pid) {
      const hit = this.positions.get(pid)
      if (hit) return hit
    }
    // `nfpmTokenId` can be promoted after initial upsert (discovery query),
    // while the secondary index is keyed on the synthetic route token id.
    // Keep lookups robust for event payloads that already carry the real id,
    // regardless of whether they arrive as a bigint or a string.
    return this.getAll().find((position) => position.nfpmTokenId?.toString() === needle)
  }

  remove(lookup: bigint | string): void {
    const found = this.find(lookup)
    if (!found) return
    this.positions.delete(found.positionId)
    this.tokenIdIndex.delete(found.tokenId.toString())
  }

  getAll(): ManualClPositionStore[] {
    return Array.from(this.positions.values())
  }

  get activePositions(): ManualClPositionStore[] {
    return this.getAll().filter((p) => !p.isBurned && p.hasLiquidity)
  }

  get totalPositions(): number {
    return this.positions.size
  }

  markHydrated(): void {
    this.isHydrated = true
  }

  /**
   * Clears every position — used when the wallet disconnects so stale user
   * data doesn't leak across sessions.
   */
  reset(): void {
    this.positions.clear()
    this.tokenIdIndex.clear()
    this.postCreatePendingUntilByTokenId.clear()
    this.isHydrated = false
    this.nfpmDiscovery = undefined
  }
}
